/**
 * API-Client für Flask Backend
 * 
 * Zentraler HTTP-Client mit:
 * - CSRF-Token-Integration
 * - Session-Cookie-Handling
 * - Error-Handling
 * - Automatischer Redirect bei 401
 */

import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getSupabaseClient } from './supabase-client';

// CSRF-Token Cache
let csrfTokenCache: string | null = null;

/**
 * Axios-Instanz mit Standard-Konfiguration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: '', // Vite Proxy leitet /api an Flask weiter
  withCredentials: true, // Wichtig für Flask-Session-Cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * CSRF-Token aus Cache oder API holen
 * Exportiert für Verwendung in FormData-Requests
 */
export async function getCsrfToken(): Promise<string | null> {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  try {
    const response = await axios.get('/api/v2/csrf/token', {
      withCredentials: true,
    });
    csrfTokenCache = response.data.csrf_token;
    return csrfTokenCache;
  } catch (error) {
    console.error('CSRF-Token konnte nicht geladen werden:', error);
    return null;
  }
}

/**
 * CSRF-Token-Cache invalidieren (z.B. nach Logout)
 */
export function invalidateCsrfToken(): void {
  csrfTokenCache = null;
}

/**
 * Request Interceptor: CSRF-Token und Auth-Token hinzufügen
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Supabase Auth Token aus Supabase Client (falls vorhanden)
    // Wird automatisch von Supabase Client verwaltet
    if (typeof window !== 'undefined') {
      try {
        const supabase = await getSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.access_token && config.headers) {
          config.headers['Authorization'] = `Bearer ${session.access_token}`;
        }
      } catch (e) {
        // Ignore errors (z.B. wenn Supabase nicht konfiguriert ist)
      }
    }

    // Let axios set Content-Type automatically for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // CSRF-Token für mutating Requests (POST, PUT, DELETE, PATCH)
    const methodsRequiringCsrf = ['post', 'put', 'delete', 'patch'];

    if (config.method && methodsRequiringCsrf.includes(config.method.toLowerCase())) {
      // ALWAYS send CSRF token for state-changing requests
      // Defense-in-Depth: Bearer token (authentication) + CSRF token (anti-CSRF)
      const csrfToken = await getCsrfToken();
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * API Error Interface
 */
export interface ApiError {
  message: string;
  status: number;
  code?: string;
  /**
   * Rohe Fehlerantwort des Servers. Ohne sie geht alles verloren, was neben
   * `error` im Body steht — etwa die `violations` eines 409 aus dem
   * Freigabe-Kontrakt, die dem Admin erst sagen, welches Kriterium fehlt.
   */
  details?: Record<string, unknown>;
}

/**
 * Feature Limit Error - returned on 402 when feature limit exceeded
 */
export interface FeatureLimitError extends ApiError {
  code: 'FEATURE_LIMIT_EXCEEDED';
  details: {
    feature_code: string;
    limit: number | boolean | string;
    current: number | null;
    tier: string;
  };
  upgrade_cta: {
    title: string;
    description: string;
    url: string;
    price: string;
  };
}

/**
 * Type guard to check if error is a feature limit error
 */
export function isFeatureLimitError(error: ApiError): error is FeatureLimitError {
  return error.code === 'FEATURE_LIMIT_EXCEEDED';
}

/**
 * Response Interceptor: Error Handling
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string }>) => {
    // 401: Nicht authentifiziert → Redirect zu Login
    // ABER: Nicht bei Auth-Endpoints, User-Status-Checks, oder wenn bereits auf Login-Seite
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;
      
      // Kein Redirect wenn:
      // 1. Auth/User-Status Endpoints (die werden beim Init aufgerufen)
      // 2. Bereits auf Login-Seite
      const isAuthOrUserEndpoint = requestUrl.startsWith('/api/v2/auth/') ||
                                   requestUrl.startsWith('/api/v2/csrf/') ||
                                   requestUrl.startsWith('/api/config/') ||
                                   requestUrl.includes('/api/user/me');
      const isOnLoginPage = currentPath.startsWith('/v2/login') || currentPath.startsWith('/reset-password');

      if (isAuthOrUserEndpoint || isOnLoginPage) {
        return Promise.reject(error);
      }

      // Redirect zu Login mit "next" Parameter
      const loginPath = '/v2/login';
      window.location.href = `${loginPath}?next=${encodeURIComponent(currentPath)}`;
      return Promise.reject(error);
    }

    // 403: Keine Berechtigung
    if (error.response?.status === 403) {
      const message = error.response.data?.error || 'Keine Berechtigung';
      const apiError: ApiError = {
        message,
        status: 403,
        code: 'FORBIDDEN',
      };
      throw apiError;
    }

    // 422: Validierungsfehler
    if (error.response?.status === 422) {
      const message = error.response.data?.error || 'Validierungsfehler';
      const apiError: ApiError = {
        message,
        status: 422,
        code: 'VALIDATION_ERROR',
      };
      throw apiError;
    }

    // 402: Payment Required (credits or feature limit exceeded)
    if (error.response?.status === 402) {
      const data = error.response.data as Record<string, unknown>;
      const errorCode = data?.code || data?.error_code;

      // Feature limit exceeded - includes upgrade CTA
      if (errorCode === 'FEATURE_LIMIT_EXCEEDED') {
        const featureLimitError: FeatureLimitError = {
          message: (data?.message as string) || 'Feature limit reached',
          status: 402,
          code: 'FEATURE_LIMIT_EXCEEDED',
          details: data?.details as FeatureLimitError['details'],
          upgrade_cta: data?.upgrade_cta as FeatureLimitError['upgrade_cta'],
        };
        throw featureLimitError;
      }

      // Standard credits error
      const message = (data?.error as string) || 'Nicht genügend Guthaben';
      const apiError: ApiError = {
        message,
        status: 402,
        code: 'INSUFFICIENT_CREDITS',
      };
      throw apiError;
    }

    // Andere Fehler
    const message = error.response?.data?.error || error.response?.data?.message || error.message || 'API-Fehler';
    const apiError: ApiError = {
      message,
      status: error.response?.status || 500,
      code: 'API_ERROR',
      details: error.response?.data as Record<string, unknown> | undefined,
    };
    throw apiError;
  }
);

export { apiClient };
export default apiClient;

